import React, { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { CButton, CSpinner } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilCloudDownload, cilCloudUpload, cilCheckCircle, cilLockLocked, cilSync } from '@coreui/icons';
import { toast } from 'react-toastify';
import './SuspensionGuard.css';

const SuspensionGuard = ({ children, isAuthenticated }) => {
    const location = useLocation();
    const [uploading, setUploading] = useState(false);
    const [checking, setChecking] = useState(false);

    // Инициализируем состояние из памяти браузера (для мгновенного рендера)
    const isSuperuser = localStorage.getItem("is_superuser") === "true";
    const [isSuspended, setIsSuspended] = useState(localStorage.getItem("is_suspended") === "true");
    const [orgData, setOrgData] = useState(() => {
        try { return JSON.parse(localStorage.getItem("org_data") || "{}"); }
        catch (e) { return {}; }
    });

    // Фоновая проверка актуального статуса на сервере
    const verifyStatusWithServer = async (manualCheck = false) => {
        if (!isAuthenticated) return;
        if (manualCheck) setChecking(true);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/profile/', {
                headers: { Authorization: `Bearer ${token}` }
            });

            const currentOrg = res.data.organization;
            if (currentOrg) {
                // Обновляем React-состояния
                setIsSuspended(currentOrg.is_suspended);
                setOrgData(currentOrg);

                // Обновляем память браузера, чтобы при F5 статус был актуальным
                localStorage.setItem("is_suspended", currentOrg.is_suspended);
                localStorage.setItem("org_data", JSON.stringify(currentOrg));

                if (manualCheck) {
                    if (!currentOrg.is_suspended) {
                        toast.success("Оплата подтверждена! Доступ восстановлен.");
                    } else {
                        toast.warn("Оплата пока не поступила или проверяется администратором.");
                    }
                }
            }
        } catch (err) {
            console.error("Failed to verify suspension status", err);
            if (manualCheck) toast.error("Ошибка связи с сервером.");
        } finally {
            if (manualCheck) setChecking(false);
        }
    };

    // Проверяем статус при загрузке страницы и смене URL
    useEffect(() => {
        verifyStatusWithServer(false);
    }, [isAuthenticated, location.pathname]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('receipt', file);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/organization/upload-receipt/', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            toast.success("Платежное поручение успешно загружено!");

            // Обновляем локальные данные, чтобы сразу появилась ссылка на файл
            const updatedOrg = { ...orgData, receipt_url: res.data.url };
            setOrgData(updatedOrg);
            localStorage.setItem("org_data", JSON.stringify(updatedOrg));
        } catch (err) {
            toast.error("Ошибка при загрузке документа.");
        } finally {
            setUploading(false);
        }
    };

    const shouldBlock = isSuspended && !isSuperuser;

    // Принудительный редирект на дашборд
    if (shouldBlock && location.pathname !== '/dashboard') {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <>
            <div className={shouldBlock ? "suspension-blur-wrapper" : ""}>
                {children}
            </div>

            {shouldBlock && (
                <div className="suspension-overlay">
                    <div className="suspension-card shadow-lg">
                        <div className="suspension-icon mb-3">
                            <CIcon icon={cilLockLocked} size="3xl" className="text-danger" />
                        </div>
                        <h3 className="fw-bold text-dark mb-2">Доступ к кабинету временно приостановлен</h3>
                        <h6 className="text-danger fw-bold mb-4">
                            Ожидается оплата поставленного оборудования ({orgData.name || "Ваша организация"})
                        </h6>

                        <p className="text-muted small mb-4 px-3" style={{ lineHeight: '1.6' }}>
                            Доступ к функциям мониторинга, отчетам и видеонаблюдению временно ограничен до погашения задолженности по счету за оборудование.<br /><br />
                            <strong>Сбор данных не остановлен:</strong> все датчики и терминалы на технике продолжают передавать телеметрию, треки и фотоконтроль на сервер в штатном режиме. Все накопленные данные и полный функционал станут доступны сразу после подтверждения оплаты.
                        </p>

                        <div className="suspension-docs bg-light p-3 rounded border mb-4">
                            <h6 className="fw-bold mb-3 small text-uppercase text-muted">Документы для оплаты:</h6>
                            <div className="d-flex flex-wrap gap-2 justify-content-center">
                                {orgData.contract_url ? (
                                    <CButton color="secondary" variant="outline" href={orgData.contract_url} target="_blank">
                                        <CIcon icon={cilCloudDownload} className="me-2" /> Договор
                                    </CButton>
                                ) : (
                                    <span className="badge bg-secondary text-white p-2">Договор не прикреплен</span>
                                )}

                                {orgData.invoice_url ? (
                                    <CButton color="primary" href={orgData.invoice_url} target="_blank">
                                        <CIcon icon={cilCloudDownload} className="me-2" /> Счет на оплату
                                    </CButton>
                                ) : (
                                    <span className="badge bg-secondary text-white p-2">Счет не прикреплен</span>
                                )}
                            </div>
                        </div>

                        <div className="suspension-upload border-top pt-4">
                            {orgData.receipt_url ? (
                                <div className="d-flex flex-column align-items-center">
                                    <div className="text-success fw-bold mb-2 d-flex align-items-center">
                                        <CIcon icon={cilCheckCircle} className="me-2" size="lg" /> Платежное поручение отправлено!
                                    </div>
                                    <div className="d-flex flex-wrap justify-content-center gap-2 mt-2">
                                        <CButton color="success" variant="outline" size="sm" href={orgData.receipt_url} target="_blank">
                                            Посмотреть файл
                                        </CButton>
                                        <label className="btn btn-sm btn-light border mb-0" style={{ cursor: 'pointer' }}>
                                            {uploading ? <CSpinner size="sm" /> : "Загрузить другой"}
                                            <input type="file" hidden onChange={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png" />
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div className="d-flex flex-column align-items-center mb-3">
                                    <p className="small text-muted fw-bold mb-2">Вы произвели оплату?</p>
                                    <label className="btn btn-success text-white fw-bold shadow-sm px-4 py-2" style={{ cursor: 'pointer' }}>
                                        {uploading ? <CSpinner size="sm" className="me-2" /> : <CIcon icon={cilCloudUpload} className="me-2" />}
                                        {uploading ? "Загрузка..." : "Загрузить платежное поручение"}
                                        <input type="file" hidden onChange={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png" />
                                    </label>
                                </div>
                            )}

                            {/* Кнопка обновления статуса */}
                            <div className="mt-4 pt-2">
                                <CButton
                                    color="secondary"
                                    variant="outline"
                                    size="sm"
                                    className="fw-bold shadow-sm"
                                    onClick={() => verifyStatusWithServer(true)}
                                    disabled={checking}
                                >
                                    {checking ? <CSpinner size="sm" className="me-2" /> : <CIcon icon={cilSync} className="me-1" />}
                                    Проверить статус оплаты
                                </CButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SuspensionGuard;